/**
 * Do guests who record their voice go on to become members more often?
 *
 * ⚠️ THE PROTOTYPE SAYS "2.3×". THAT NUMBER IS AN ILLUSTRATION AND IS NEVER
 * RENDERED HERE. This card prints the multiple the server measured, or it says
 * plainly that there is no multiple to print. Three honest outcomes:
 *
 *   · a real multiple, with the two rates it came from and the sample it was
 *     measured across;
 *   · `multiple: null` — no text-only guest has converted, so there is nothing
 *     to be a multiple OF. Division by that would be an infinity dressed as a
 *     marketing claim;
 *   · a sample too small to carry a claim, which is stated beside the number
 *     rather than left for the reader to work out.
 *
 * The measurement is ALL TIME, not the selected range — a conversion can happen
 * weeks after the recording, and windowing it would systematically undercount
 * the very behaviour being measured. The card says so.
 */

import React from 'react';
import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { num } from '../weddingFormat';
import type { VoiceInsight } from '../weddingApi';

/** Under this, two rates are noise dressed as a finding. */
const SMALL_SAMPLE = 100;

function pct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 1000) / 10}%`;
}

const VoiceInsightCard: React.FC<{ insight: VoiceInsight | undefined }> = ({ insight }) => {
  if (!insight) return null;

  const sample = Number(insight.sample_size || 0);
  const voiceGuests = num(insight.voice_guests);
  const textGuests = num(insight.text_guests);

  if (sample === 0 || (voiceGuests === 0 && textGuests === 0)) {
    return (
      <Alert severity="info" variant="outlined" icon={<GraphicEqIcon />} sx={{ mt: 2 }}>
        <AlertTitle>Voice and conversion</AlertTitle>
        No guest has contributed yet, so there is nothing to compare. This measurement needs guests
        on both sides — some who recorded their voice and some who only wrote.
      </Alert>
    );
  }

  const small = sample < SMALL_SAMPLE;

  return (
    <Alert
      severity={insight.multiple === null ? 'info' : 'success'}
      variant="outlined"
      icon={<GraphicEqIcon />}
      sx={{ mt: 2 }}
    >
      <AlertTitle>Voice and conversion</AlertTitle>
      {insight.multiple === null ? (
        <Typography variant="body2">
          No multiple can be reported: {textGuests === 0
            ? 'no text-only guest has contributed yet'
            : 'no text-only guest has become a member yet'}, so there is nothing for the voice rate
          to be a multiple of. Guests who recorded their voice convert at {pct(insight.voice_rate)};
          text-only guests at {pct(insight.text_rate)}.
        </Typography>
      ) : (
        <Typography variant="body2">
          Guests who added a voice recording convert at <strong>{insight.multiple}×</strong> the rate
          of text-only contributors — {pct(insight.voice_rate)} against {pct(insight.text_rate)}.
        </Typography>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Voice guests
          </Typography>
          <Typography variant="body2">
            {voiceGuests.toLocaleString()} · {num(insight.voice_converted).toLocaleString()} joined
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Text-only guests
          </Typography>
          <Typography variant="body2">
            {textGuests.toLocaleString()} · {num(insight.text_converted).toLocaleString()} joined
          </Typography>
        </Box>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {small
          ? `Measured across only ${sample.toLocaleString()} guests — too few to act on yet. `
          : `Measured across ${sample.toLocaleString()} guests. `}
        {insight.note}
      </Typography>
    </Alert>
  );
};

export default VoiceInsightCard;
